/* The recap screen, the «О компании» answers and the personal onboarding —
 * the company-side billing calls these surfaces need (spec
 * 20261001_subscription_constructor_plan_ru.md §6–§8). The constructor and the
 * «Тариф» page have their own client in `billing.ts`; this one only reads the
 * overview and the recap, saves the size answers and asks for the onboarding.
 *
 * Money arrives as strings ("19.00"); dates as ISO.
 */

import { api } from "./api";
import { FACT_KEY, ITEM_NAME_KEY, PRESET_NAME_KEY, type Overview as BillingOverview } from "./billing";

export type Money = string;

export type MeteredQuote = {
  count: number;
  included: number | null;
  unlimited: boolean;
  over: number;
  unit_price: Money;
  amount: Money;
  charged: boolean;
};

export type Quote = {
  codes: string[];
  lines: { code: string; price: Money }[];
  period_months: number;
  discount_percent: Money;
  fixed: Money;
  fixed_net: Money;
  orders: MeteredQuote;
  postings: MeteredQuote;
  usage: Money;
  total: Money;
  prepay_total: Money;
};

export type Offer = { key: "own" | "bundle" | "nolimit"; total: Money; best: boolean; quote: Quote };

export type ModuleUse = { code: string; used: boolean; facts: { key: string; count: number }[] };

export type Metered = {
  orders: number;
  by_channel: { client: number; quick: number; seller: number };
  postings: number;
};

export type Usage = {
  period: string;
  start: string;
  end: string;
  metered: Metered;
  modules: ModuleUse[];
  codes: string[];
  quote: Quote;
  offers: Offer[];
};

export type RecapState = {
  show: "pre" | "end" | null;
  term_ends_at: string | null;
  term_ended_at: string | null;
  days_left: number;
};

export type PriceBookItem = {
  code: string;
  kind: string;
  price: Money;
  included_orders: number | null;
  included_postings: number | null;
  grants: string[];
};

export type CompanySize = {
  premises_area: string;
  annual_turnover: string;
  staff_count: string;
  monthly_orders: string;
};

/** Same shape as the «Тариф» page's, so a card's result can go straight into its overview. */
export type Recommendation = BillingOverview["recommended"];

export type Overview = {
  subscription: {
    plan: string;
    status: string;
    is_pro: boolean;
    trial_ends_at: string | null;
    items: { code: string; unit_price: Money; started_on: string; ends_on: string | null }[];
    [key: string]: unknown;
  };
  price_book: PriceBookItem[];
  period_discounts: Record<string, number>;
  presets: Record<string, string[]>;
  feature_codes: Record<string, string>;
  usage: Usage;
  size: CompanySize;
  recommended: Recommendation;
  recap: RecapState;
  open_statements: number;
};

export type Recap = RecapState & {
  trial: boolean;
  start: string;
  end: string;
  metered: Metered;
  modules: ModuleUse[];
  codes: string[];
  quote: Quote;
  offers: Offer[];
};

export type ServiceStatus = "requested" | "invoiced" | "in_progress" | "done" | "cancelled";

export type ServiceOrder = {
  id: number;
  code: string;
  status: ServiceStatus;
  price: Money;
  created_at: string;
  started_on: string | null;
  expires_on: string | null;
  hours_included: number;
  hours_spent: Money | number;
  checklist: { id: string; done: boolean; done_at: string | null }[];
  statement_id: number | null;
  contact_name: string;
  contact_phone: string;
  comment: string;
};

export const fetchOverview = (slug: string) => api.get<Overview>(`/t/${slug}/billing/overview`);
export const fetchRecap = (slug: string) => api.get<Recap>(`/t/${slug}/billing/recap`);
export const markRecapSeen = (slug: string, kind: "pre" | "end") =>
  api.post<RecapState>(`/t/${slug}/billing/recap/seen`, { kind });
export const saveCompanySize = (slug: string, size: CompanySize) =>
  api.put<{ size: CompanySize; recommended: Recommendation }>(`/t/${slug}/billing/company-size`, size);
export const listServices = (slug: string) => api.get<ServiceOrder[]>(`/t/${slug}/billing/services`);
export const requestOnboarding = (
  slug: string,
  body: { contact_name: string; contact_phone: string; comment: string },
) => api.post<ServiceOrder>(`/t/${slug}/billing/services`, { code: "onboarding", ...body });

/* ── Opening the recap on demand ─────────────────────────────────────────── */

/** The Dashboard's trial banner opens the recap figures before the screen is
 *  due; `TermRecap` (mounted once in App.svelte) listens for this. Opened this
 *  way, closing it does not count as "seen". */
export const OPEN_RECAP_EVENT = "oe:open-recap";
export function openRecap(slug: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_RECAP_EVENT, { detail: { slug } }));
}

/* ── Vocabulary ──────────────────────────────────────────────────────────── */
// Literal keys only: the string extractor reads them from source.

/** Base and modules in the constructor's order. */
export const ITEM_CODES = ["base", "m_page", "m_stock", "m_acc", "m_rep", "m_dash", "m_find"] as const;
export const PACKAGE_CODES = ["bundle", "nolimit"];

/* Item, preset and usage-fact names are the «Тариф» page's keys (`billing.ts`),
   so one item is called one thing everywhere. Fact strings take `{count}`. */
export const ITEM_LABEL: Record<string, string> = ITEM_NAME_KEY;
export const PRESET_LABEL: Record<string, string> = PRESET_NAME_KEY;
export const FACT_LABEL: Record<string, string> = FACT_KEY;

/** The four «О компании» questions (spec §7) with the values the backend
 *  accepts; "" means not answered. */
export type SizeQuestion = {
  field: keyof CompanySize;
  labelKey: string;
  options: { value: string; labelKey: string }[];
};

export const SIZE_QUESTIONS: SizeQuestion[] = [
  {
    field: "premises_area",
    labelKey: "billing.size.area.q",
    options: [
      { value: "0", labelKey: "billing.size.area.none" },
      { value: "<100", labelKey: "billing.size.area.lt100" },
      { value: "100-500", labelKey: "billing.size.area.100_500" },
      { value: "500-2000", labelKey: "billing.size.area.500_2000" },
      { value: "2000+", labelKey: "billing.size.area.gt2000" },
    ],
  },
  {
    field: "annual_turnover",
    labelKey: "billing.size.turnover.q",
    options: [
      { value: "<100k", labelKey: "billing.size.turnover.lt100k" },
      { value: "<500k", labelKey: "billing.size.turnover.100k_500k" },
      { value: "<1m", labelKey: "billing.size.turnover.500k_1m" },
      { value: "<5m", labelKey: "billing.size.turnover.1m_5m" },
      { value: "5m+", labelKey: "billing.size.turnover.gt5m" },
    ],
  },
  {
    field: "staff_count",
    labelKey: "billing.size.staff.q",
    options: [
      { value: "1", labelKey: "billing.size.staff.1" },
      { value: "2-10", labelKey: "billing.size.staff.2_10" },
      { value: "11-50", labelKey: "billing.size.staff.11_50" },
      { value: "51-200", labelKey: "billing.size.staff.51_200" },
      { value: "200+", labelKey: "billing.size.staff.gt200" },
    ],
  },
  {
    field: "monthly_orders",
    labelKey: "billing.size.orders.q",
    options: [
      { value: "<50", labelKey: "billing.size.orders.lt50" },
      { value: "50-200", labelKey: "billing.size.orders.50_200" },
      { value: "200-1000", labelKey: "billing.size.orders.200_1000" },
      { value: "1000+", labelKey: "billing.size.orders.gt1000" },
    ],
  },
];

export const EMPTY_SIZE: CompanySize = {
  premises_area: "",
  annual_turnover: "",
  staff_count: "",
  monthly_orders: "",
};

/** The eleven steps of the personal onboarding (spec §8.1), ids as the
 *  server's checklist (`services/onboarding.CHECKLIST`). */
export const ONBOARDING_STEPS: { id: string; labelKey: string; descKey: string }[] = [
  { id: "kickoff", labelKey: "billing.onb.step.kickoff", descKey: "billing.onb.stepDesc.kickoff" },
  { id: "profile", labelKey: "billing.onb.step.profile", descKey: "billing.onb.stepDesc.profile" },
  { id: "products", labelKey: "billing.onb.step.products", descKey: "billing.onb.stepDesc.products" },
  { id: "prices", labelKey: "billing.onb.step.prices", descKey: "billing.onb.stepDesc.prices" },
  { id: "partners", labelKey: "billing.onb.step.partners", descKey: "billing.onb.stepDesc.partners" },
  { id: "stock", labelKey: "billing.onb.step.stock", descKey: "billing.onb.stepDesc.stock" },
  { id: "public_page", labelKey: "billing.onb.step.public_page", descKey: "billing.onb.stepDesc.public_page" },
  { id: "training", labelKey: "billing.onb.step.training", descKey: "billing.onb.stepDesc.training" },
  { id: "test_run", labelKey: "billing.onb.step.test_run", descKey: "billing.onb.stepDesc.test_run" },
  { id: "custom", labelKey: "billing.onb.step.custom", descKey: "billing.onb.stepDesc.custom" },
  { id: "go_live", labelKey: "billing.onb.step.go_live", descKey: "billing.onb.stepDesc.go_live" },
];

export const SERVICE_STATUS_LABEL: Record<string, string> = {
  requested: "billing.onb.status.requested",
  invoiced: "billing.onb.status.invoiced",
  in_progress: "billing.onb.status.in_progress",
  done: "billing.onb.status.done",
  cancelled: "billing.onb.status.cancelled",
};

/** Where the onboarding card lives: Settings → «Начало работы». */
export const onboardingCardHref = (slug: string) => `/t/${slug}/account/onboarding?service=onboarding`;

/** Monthly price of a set from the price book (EUR, without VAT). */
export function setPrice(codes: string[], book: PriceBookItem[]): number {
  return codes.reduce((sum, code) => sum + Number(book.find((i) => i.code === code)?.price ?? 0), 0);
}

export function priceOf(code: string, book: PriceBookItem[] | undefined): number | null {
  const item = book?.find((i) => i.code === code);
  return item ? Number(item.price) : null;
}
